import type { YearsMonthsDurationValue } from '../components/YearsMonthsDurationInput';
import type { WorkHistoryEntryDraft } from './careerHistorySurveyTypes';

export function dustDurationPartsFromDraft(draft: {
  dustDurationYears: string;
  dustDurationMonths: string;
}): YearsMonthsDurationValue {
  return {
    years: draft.dustDurationYears,
    months: draft.dustDurationMonths,
  };
}

function parseIsoDate(iso: string): Date | null {
  const m = iso.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Calendar years/months between two ISO dates (end inclusive of partial month when day >= start day). */
export function durationPartsBetweenIso(startIso: string, endIso: string): YearsMonthsDurationValue {
  const start = parseIsoDate(startIso);
  const end = parseIsoDate(endIso);
  if (!start || !end || end < start) return { years: '', months: '' };

  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();
  if (end.getDate() < start.getDate()) months -= 1;
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  if (years < 0) return { years: '', months: '' };

  return {
    years: years > 0 ? String(years) : '',
    months: months > 0 ? String(months) : '',
  };
}

/** Derive dust duration from start/end (or start→today when current job). */
export function withAutoDustDuration(
  draft: WorkHistoryEntryDraft,
  locale: string,
  asOfIso?: string
): WorkHistoryEntryDraft {
  if (!draft.startDate) {
    return {
      ...draft,
      dustDuration: '',
      dustDurationYears: '',
      dustDurationMonths: '',
      dustDurationSkipped: true,
    };
  }

  const endIso =
    draft.endDateSkipped || !draft.endDate ? (asOfIso ?? todayIso()) : draft.endDate;
  const parts = durationPartsBetweenIso(draft.startDate, endIso);
  const formatted = formatDustDuration(parts.years, parts.months, locale);
  if (!formatted) {
    return {
      ...draft,
      dustDuration: '',
      dustDurationYears: '',
      dustDurationMonths: '',
      dustDurationSkipped: true,
    };
  }

  return {
    ...draft,
    dustDurationYears: parts.years,
    dustDurationMonths: parts.months,
    dustDuration: formatted,
    dustDurationSkipped: false,
  };
}

export function formatDustDuration(years: string, months: string, locale: string): string {
  const y = years.trim() === '' ? 0 : Number(years);
  const m = months.trim() === '' ? 0 : Number(months);
  if (y === 0 && m === 0) return '';

  if (locale.startsWith('ja')) {
    if (y > 0 && m > 0) return `${y}年${m}ヶ月`;
    if (y > 0) return `${y}年`;
    return `${m}ヶ月`;
  }
  if (locale.startsWith('vi')) {
    if (y > 0 && m > 0) return `${y} năm ${m} tháng`;
    if (y > 0) return `${y} năm`;
    return `${m} tháng`;
  }
  if (y > 0 && m > 0) return `${y} year${y === 1 ? '' : 's'} ${m} month${m === 1 ? '' : 's'}`;
  if (y > 0) return `${y} year${y === 1 ? '' : 's'}`;
  return `${m} month${m === 1 ? '' : 's'}`;
}

/** Best-effort parse of stored dust_duration TEXT back into year/month fields. */
export function parseDustDuration(text: string): YearsMonthsDurationValue {
  const raw = text.trim();
  if (!raw) return { years: '', months: '' };

  const ja = raw.match(/^(\d+)年(?:(\d+)ヶ月)?$/u);
  if (ja) {
    return { years: ja[1] ?? '', months: ja[2] ?? '' };
  }
  const jaMonthsOnly = raw.match(/^(\d+)ヶ月$/u);
  if (jaMonthsOnly) {
    return { years: '', months: jaMonthsOnly[1] ?? '' };
  }

  const vi = raw.match(/^(\d+)\s*năm(?:\s+(\d+)\s*tháng)?$/iu);
  if (vi) {
    return { years: vi[1] ?? '', months: vi[2] ?? '' };
  }
  const viMonthsOnly = raw.match(/^(\d+)\s*tháng$/iu);
  if (viMonthsOnly) {
    return { years: '', months: viMonthsOnly[1] ?? '' };
  }

  const en = raw.match(/^(\d+)\s*years?\s+(\d+)\s*months?$/iu);
  if (en) {
    return { years: en[1] ?? '', months: en[2] ?? '' };
  }
  const enYearsOnly = raw.match(/^(\d+)\s*years?$/iu);
  if (enYearsOnly) {
    return { years: enYearsOnly[1] ?? '', months: '' };
  }
  const enMonthsOnly = raw.match(/^(\d+)\s*months?$/iu);
  if (enMonthsOnly) {
    return { years: '', months: enMonthsOnly[1] ?? '' };
  }

  return { years: '', months: '' };
}
